import { useMemo, useRef, useState } from "react";
import { Package, Pencil, Truck, Upload } from "lucide-react";

import {
  DatePicker,
  DateRangePicker,
  fromIsoDate,
  toIsoDate,
  type DateRange,
} from "@/components/cinch/DatePicker";
import { EmptyState } from "@/components/cinch/EmptyState";
import { Field } from "@/components/cinch/Field";
import {
  FilterBar,
  FilterChip,
  FilterClearAll,
  FilterSearch,
  MultiSelectChip,
} from "@/components/cinch/FilterBar";
import { IconButton } from "@/components/cinch/IconButton";
import { LinkOut } from "@/components/cinch/LinkOut";
import { NumberInput } from "@/components/cinch/NumberInput";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

import {
  CARRIER_LABEL,
  SHIPMENT_LABEL,
  fmtDate,
  fmtMoney,
  useDeleteAllocation,
  useDeletePurchase,
  useDepartments,
  useImportWorkbook,
  useItems,
  useLedger,
  usePurchases,
  useSaveItem,
  useSavePurchase,
  useSetAllocation,
  useShipments,
  useSummary,
  useUpdateShipment,
  useUploadPhoto,
  type Carrier,
  type Disbursement,
  type Item,
  type Purchase,
  type Shipment,
  type ShipmentStatus,
} from "../api";

import { ItemDialog } from "./ItemDialog";
import { ShipmentDialog } from "./ShipmentDialog";

/** Admin tab — load purchases, earmark stock to departments, run shipping. */
export function AdminView() {
  return (
    <Tabs defaultValue="purchases">
      <TabsList variant="segmented">
        <TabsTrigger value="purchases">Purchases</TabsTrigger>
        <TabsTrigger value="shipping">Shipping</TabsTrigger>
        <TabsTrigger value="catalog">Catalog</TabsTrigger>
        <TabsTrigger value="import">Import</TabsTrigger>
      </TabsList>
      <TabsContent value="purchases" className="mt-4">
        <PurchasesPanel />
      </TabsContent>
      <TabsContent value="shipping" className="mt-4">
        <ShippingPanel />
      </TabsContent>
      <TabsContent value="catalog" className="mt-4">
        <CatalogPanel />
      </TabsContent>
      <TabsContent value="import" className="mt-4">
        <ImportPanel />
      </TabsContent>
    </Tabs>
  );
}

// ── Purchases ───────────────────────────────────────────────────────────────

function PurchasesPanel() {
  const items = useItems();
  const purchases = usePurchases();
  const save = useSavePurchase();
  const upload = useUploadPhoto();
  const fileRef = useRef<HTMLInputElement>(null);

  const [itemId, setItemId] = useState("");
  const [quantity, setQuantity] = useState<number | null>(1);
  const [unitCost, setUnitCost] = useState<number | null>(null);
  const [freight, setFreight] = useState<number | null>(null);
  const [purchaseDate, setPurchaseDate] = useState("");
  const [vendor, setVendor] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  const reset = () => {
    setQuantity(1);
    setUnitCost(null);
    setFreight(null);
    setPurchaseDate("");
    setVendor("");
    setReference("");
    setNotes("");
    setPhotoUrl(null);
  };

  const ready = Boolean(itemId) && (quantity ?? 0) > 0 && unitCost != null;

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Load a swag purchase</CardTitle>
          <p className="text-sm text-muted-foreground">
            One order = one row: how many arrived, what each cost, and a photo so
            people know what they’re asking for. Stock on hand is computed from
            this minus what’s gone out — there’s no count to keep in sync.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="po-item">Item</Label>
              <Select value={itemId} onValueChange={setItemId}>
                <SelectTrigger id="po-item">
                  <SelectValue placeholder="Pick an item" />
                </SelectTrigger>
                <SelectContent>
                  {(items.data ?? []).map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <NumberInput
              id="po-qty"
              label="Quantity"
              maxDecimals={0}
              value={quantity}
              onChange={setQuantity}
            />
            <NumberInput
              id="po-cost"
              label="Cost per unit"
              prefix="$"
              value={unitCost}
              onChange={setUnitCost}
            />
            <NumberInput
              id="po-freight"
              label="Inbound freight (optional)"
              prefix="$"
              value={freight}
              onChange={setFreight}
              helper="Freight on the order itself, not outbound gift shipping"
            />
            {/* No wrapping <Label>: DatePicker renders `aria-label` as its own
                floating label, so adding one shows the text twice. */}
            <DatePicker
              id="po-date"
              value={fromIsoDate(purchaseDate)}
              onChange={(d) => setPurchaseDate(d ? toIsoDate(d) : "")}
              aria-label="Purchase date"
            />
            <Field
              id="po-vendor"
              label="Vendor"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
            />
            <Field
              id="po-ref"
              label="Order / invoice reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>

          <Textarea
            placeholder="Notes (colour, size, where it's stored…)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />

          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  upload.mutate(file, { onSuccess: (r) => setPhotoUrl(r.url) });
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={upload.isPending}
            >
              <Upload className="mr-1 h-4 w-4" />
              {upload.isPending ? "Uploading…" : "Add photo"}
            </Button>
            {photoUrl && (
              <img
                src={photoUrl}
                alt="Swag order"
                className="h-14 w-14 rounded-lg border object-cover"
              />
            )}
            <Button
              className="ml-auto"
              disabled={!ready || save.isPending}
              onClick={() =>
                save.mutate(
                  {
                    body: {
                      item_id: itemId,
                      quantity: quantity ?? 1,
                      unit_cost: unitCost ?? 0,
                      freight_cost: freight,
                      purchase_date: purchaseDate || null,
                      vendor: vendor || null,
                      order_reference: reference || null,
                      notes: notes || null,
                      photo_url: photoUrl,
                    },
                  },
                  { onSuccess: reset },
                )
              }
            >
              {save.isPending ? "Saving…" : "Add purchase"}
            </Button>
          </div>
          {save.error && <Alert variant="danger">{String(save.error)}</Alert>}
          {upload.error && <Alert variant="danger">{String(upload.error)}</Alert>}
        </CardContent>
      </Card>

      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">Purchase orders</h3>
        {purchases.isLoading ? (
          <Skeleton className="h-40" />
        ) : (purchases.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing loaded yet.</p>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {(purchases.data ?? []).map((lot) => (
              <PurchaseCard key={lot.id} lot={lot} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PurchaseCard({ lot }: { lot: Purchase }) {
  const departments = useDepartments();
  const setAllocation = useSetAllocation();
  const deleteAllocation = useDeleteAllocation();
  const deletePurchase = useDeletePurchase();
  const [deptId, setDeptId] = useState("");
  const [qty, setQty] = useState<number | null>(1);

  const freeToEarmark = lot.on_hand - lot.reserved;
  // NumberInput accepts any number, so the bound the old `max` attribute
  // hinted at is enforced here — reserving more than exists would silently
  // over-promise stock to a department.
  const inRange = qty != null && qty >= 1 && qty <= freeToEarmark;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{lot.item_name}</CardTitle>
            <p className="text-xs text-muted-foreground">
              {lot.quantity} bought ·{" "}
              {fmtMoney(lot.unit_cost)} each · {fmtMoney(lot.total_cost)} total
              {lot.purchase_date
                ? ` · ${fmtDate(lot.purchase_date)}`
                : lot.purchase_year
                  ? ` · ${lot.purchase_year}`
                  : ""}
              {lot.vendor ? ` · ${lot.vendor}` : ""}
            </p>
          </div>
          {lot.photo_url && (
            <img
              src={lot.photo_url}
              alt={lot.item_name ?? ""}
              className="h-12 w-12 shrink-0 rounded-lg border object-cover"
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 pb-4 text-sm">
        <div className="flex flex-wrap gap-2">
          <Badge variant="success">{lot.on_hand} on hand</Badge>
          <Badge variant="neutral">{lot.disbursed} given</Badge>
          {lot.reserved > 0 && <Badge variant="info">{lot.reserved} earmarked</Badge>}
          {lot.source === "imported" && <Badge variant="neutral">Imported</Badge>}
        </div>

        {lot.allocations.length > 0 && (
          <ul className="flex flex-col gap-1">
            {lot.allocations.map((alloc) => (
              <li key={alloc.id} className="flex items-center justify-between gap-2">
                <span>
                  {alloc.department_name} — {alloc.quantity} unit
                  {alloc.quantity === 1 ? "" : "s"}
                  {alloc.note && (
                    <span className="text-muted-foreground"> · {alloc.note}</span>
                  )}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteAllocation.mutate(alloc.id)}
                >
                  Release
                </Button>
              </li>
            ))}
          </ul>
        )}

        {freeToEarmark > 0 && (
          <div className="flex flex-wrap items-end gap-2 border-t pt-3">
            <div className="flex min-w-[160px] flex-col gap-1.5">
              <Label>Earmark to</Label>
              <Select value={deptId} onValueChange={setDeptId}>
                <SelectTrigger>
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  {(departments.data ?? []).map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <NumberInput
              id={`alloc-qty-${lot.id}`}
              label="Units"
              maxDecimals={0}
              value={qty}
              onChange={setQty}
              className="w-24"
            />
            <Button
              size="sm"
              disabled={!deptId || !inRange || setAllocation.isPending}
              onClick={() =>
                setAllocation.mutate({
                  purchaseId: lot.id,
                  department_id: deptId,
                  quantity: qty ?? 1,
                })
              }
            >
              Reserve
            </Button>
            <span className="text-xs text-muted-foreground">
              {freeToEarmark} free to earmark
            </span>
          </div>
        )}

        {lot.disbursed === 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="self-start text-danger"
            onClick={() => deletePurchase.mutate(lot.id)}
          >
            Delete this order
          </Button>
        )}
        {setAllocation.error && (
          <Alert variant="danger">{String(setAllocation.error)}</Alert>
        )}
        {deletePurchase.error && (
          <Alert variant="danger">{String(deletePurchase.error)}</Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ── Shipping ────────────────────────────────────────────────────────────────

const CARRIERS: Carrier[] = ["fedex", "usps", "ups", "dhl", "other"];
const STATUSES: ShipmentStatus[] = [
  "pending",
  "label_created",
  "in_transit",
  "delivered",
  "exception",
  "unknown",
];

/** Ledger rows heading to the same place — one box's worth. */
interface ShipGroup {
  key: string;
  name: string | null;
  address: string | null;
  phone: string | null;
  rows: Disbursement[];
}

/** Group unshipped rows by where they're going.
 *
 *  The requested address is the grouping key when there is one, because that
 *  is literally "these go in one package". Rows with no address fall back to
 *  the company, and anything with neither is its own group rather than being
 *  lumped into one meaningless pile. */
function groupForShipping(rows: Disbursement[]): ShipGroup[] {
  const norm = (s: string | null | undefined) =>
    (s ?? "").replace(/[^a-z0-9]+/gi, " ").trim().toLowerCase();
  const map = new Map<string, ShipGroup>();
  for (const row of rows) {
    const address = row.requested_ship_to_address ?? null;
    const key =
      norm(address) || norm(row.recipient_company_name) || `row:${row.id}`;
    const existing = map.get(key);
    if (existing) {
      existing.rows.push(row);
      existing.address ??= address;
      existing.phone ??= row.requested_ship_to_phone ?? null;
      continue;
    }
    map.set(key, {
      key,
      name:
        row.requested_ship_to_name ??
        row.recipient_company_name ??
        row.recipient_name ??
        null,
      address,
      phone: row.requested_ship_to_phone ?? null,
      rows: [row],
    });
  }
  // Real addresses first — those are the ones that actually need mailing.
  return [...map.values()].sort(
    (a, b) => Number(Boolean(b.address)) - Number(Boolean(a.address)),
  );
}

function ShippingPanel() {
  const summary = useSummary();
  const shipments = useShipments();
  const [shipGroup, setShipGroup] = useState<ShipGroup | null>(null);
  const [search, setSearch] = useState("");
  const [statuses, setStatuses] = useState<string[]>([]);
  const [carriers, setCarriers] = useState<string[]>([]);
  const [dates, setDates] = useState<DateRange>({ start: null, end: null });
  // The send queue is rows the requester asked to have MAILED and that have
  // no box yet — not simply "no shipment attached". Most swag is handed over
  // in person: the workbook's 15 X-Mas Raffle rows and the SEMA / Pest World
  // booth giveaways would otherwise sit in Adrey's queue forever.
  const ledger = useLedger({ limit: 500 });
  const unshipped = (ledger.data?.rows ?? []).filter(
    (r) => !r.shipment_id && r.needs_shipping,
  );
  const groups = groupForShipping(unshipped);

  // Filtering happens here rather than server-side: the shipment list is one
  // row per box (16 for the entire imported history), so it is already fully
  // loaded and a round-trip per keystroke would be slower, not faster.
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const from = dates.start ? toIsoDate(dates.start) : null;
    const to = dates.end ? toIsoDate(dates.end) : null;
    return (shipments.data ?? []).filter((s) => {
      if (statuses.length && !statuses.includes(s.status)) return false;
      if (carriers.length && !carriers.includes(s.carrier ?? "none")) return false;
      if (from && (!s.shipped_at || s.shipped_at < from)) return false;
      if (to && (!s.shipped_at || s.shipped_at > to)) return false;
      if (!needle) return true;
      return [
        ...s.recipients,
        ...s.contents,
        s.ship_to_name,
        s.ship_to_address,
        s.tracking_number,
      ]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(needle));
    });
  }, [shipments.data, search, statuses, carriers, dates]);

  const hasFilters =
    Boolean(search) ||
    statuses.length > 0 ||
    carriers.length > 0 ||
    Boolean(dates.start || dates.end);

  return (
    <div className="flex flex-col gap-5">
      {summary.data?.tracking_manual_only && (
        <Alert variant="info">
          No carrier API key is configured, so statuses are whatever you set
          here. Add <code>CINCH_OPS_FEDEX_CLIENT_ID</code> /{" "}
          <code>CINCH_OPS_USPS_CLIENT_ID</code> (plus their secrets) and the
          nightly job starts checking FedEx and USPS on its own — both developer
          accounts are free.
        </Alert>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Truck className="h-4 w-4" /> Shipments
        </h3>
        <p className="text-xs text-muted-foreground">
          {filtered.length === shipments.data?.length
            ? `${filtered.length} recorded`
            : `${filtered.length} of ${shipments.data?.length ?? 0}`}
        </p>
      </div>

      {groups.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              To send ({unshipped.length})
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Requested for shipping and not yet in a box, grouped by
              destination — everything going to one address usually travels
              together. Anything handed over in person never appears here.
            </p>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 pb-4">
            {groups.map((group) => (
              <div
                key={group.key}
                className="flex flex-wrap items-start justify-between gap-3 rounded-xl border p-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {group.name ?? "No address recorded"}
                  </p>
                  {group.address ? (
                    <p className="text-xs text-muted-foreground">{group.address}</p>
                  ) : (
                    <p className="text-xs text-warning">
                      No address on the request — ask the requester before sending.
                    </p>
                  )}
                  {group.phone ? (
                    <p className="text-xs text-muted-foreground">{group.phone}</p>
                  ) : (
                    <p className="text-xs text-warning">No phone number</p>
                  )}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {group.rows.map((row) => (
                      <Badge key={row.id} variant="neutral">
                        {row.item_name} → {row.recipient_name ?? row.occasion ?? "—"}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button size="sm" onClick={() => setShipGroup(group)}>
                  <Truck className="mr-1 h-4 w-4" /> Record shipment
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <ShipmentDialog
        open={shipGroup !== null}
        onOpenChange={(next) => !next && setShipGroup(null)}
        candidates={shipGroup?.rows ?? []}
        defaultAddress={shipGroup?.address}
        defaultName={shipGroup?.name}
        defaultPhone={shipGroup?.phone}
      />

      <FilterBar>
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Search recipient, address, or tracking…"
        />
        <MultiSelectChip
          label="Status"
          options={STATUSES.map((s) => ({ value: s, label: SHIPMENT_LABEL[s] }))}
          value={statuses}
          onChange={setStatuses}
        />
        <MultiSelectChip
          label="Carrier"
          options={[
            ...CARRIERS.map((c) => ({ value: c, label: CARRIER_LABEL[c] })),
            { value: "none", label: "No carrier set" },
          ]}
          value={carriers}
          onChange={setCarriers}
        />
        <FilterChip
          label="Shipped"
          valueLabel={
            dates.start || dates.end
              ? `${dates.start ? toIsoDate(dates.start) : "…"} → ${
                  dates.end ? toIsoDate(dates.end) : "…"
                }`
              : undefined
          }
        >
          <div className="space-y-2">
            <DateRangePicker value={dates} onChange={setDates} />
            {(dates.start || dates.end) && (
              <button
                type="button"
                onClick={() => setDates({ start: null, end: null })}
                className="text-xs text-brand hover:underline focus-visible:outline-none focus-visible:underline"
              >
                Clear
              </button>
            )}
          </div>
        </FilterChip>
        <div className="flex-1" />
        {hasFilters && (
          <FilterClearAll
            onClick={() => {
              setSearch("");
              setStatuses([]);
              setCarriers([]);
              setDates({ start: null, end: null });
            }}
          />
        )}
      </FilterBar>

      {shipments.isLoading ? (
        <Skeleton className="h-40" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Truck />}
          title={hasFilters ? "No shipments match" : "No shipments recorded"}
        >
          {hasFilters
            ? "Nothing here matches those filters — try clearing one."
            : "Anything given away that needs mailing shows in the send queue above; recording a box puts it here."}
        </EmptyState>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {filtered.map((shipment) => (
            <ShipmentEditor key={shipment.id} shipment={shipment} />
          ))}
        </div>
      )}
    </div>
  );
}

function ShipmentEditor({ shipment }: { shipment: Shipment }) {
  const update = useUpdateShipment();
  const [tracking, setTracking] = useState(shipment.tracking_number ?? "");
  const [cost, setCost] = useState<number | null>(shipment.shipping_cost);
  const [phone, setPhone] = useState(shipment.ship_to_phone ?? "");
  const [shippedAt, setShippedAt] = useState(shipment.shipped_at ?? "");
  const [carrier, setCarrier] = useState<string>(shipment.carrier ?? "");

  const dirty =
    tracking !== (shipment.tracking_number ?? "") ||
    cost !== shipment.shipping_cost ||
    phone !== (shipment.ship_to_phone ?? "") ||
    shippedAt !== (shipment.shipped_at ?? "") ||
    carrier !== (shipment.carrier ?? "");

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">
            {shipment.recipients.join(", ") || shipment.ship_to_name || "Shipment"}
          </CardTitle>
          <StatusBadge
            value={shipment.status}
            label={SHIPMENT_LABEL[shipment.status]}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {shipment.contents.join(", ") || "No contents"}
          {shipment.ship_to_address ? ` · ${shipment.ship_to_address}` : ""}
          {shipment.ship_to_phone ? ` · ${shipment.ship_to_phone}` : ""}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 pb-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label>Carrier</Label>
            <Select value={carrier} onValueChange={setCarrier}>
              <SelectTrigger>
                <SelectValue placeholder="Carrier" />
              </SelectTrigger>
              <SelectContent>
                {CARRIERS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {CARRIER_LABEL[c]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Field
            id={`track-${shipment.id}`}
            label="Tracking number"
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
          />
          <NumberInput
            id={`cost-${shipment.id}`}
            label="Shipping cost"
            prefix="$"
            value={cost}
            onChange={setCost}
          />
          <Field
            id={`phone-${shipment.id}`}
            label="Phone number"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <DatePicker
            id={`shipped-${shipment.id}`}
            value={fromIsoDate(shippedAt)}
            onChange={(d) => setShippedAt(d ? toIsoDate(d) : "")}
            aria-label="Shipped on"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={shipment.status}
            onValueChange={(status) =>
              update.mutate({ id: shipment.id, body: { status } })
            }
          >
            <SelectTrigger className="w-[170px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {SHIPMENT_LABEL[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            disabled={!dirty || update.isPending}
            onClick={() =>
              update.mutate({
                id: shipment.id,
                body: {
                  tracking_number: tracking || null,
                  shipping_cost: cost,
                  ship_to_phone: phone || null,
                  shipped_at: shippedAt || null,
                  carrier: carrier || null,
                },
              })
            }
          >
            Save
          </Button>
          {shipment.tracking_url && (
            <LinkOut href={shipment.tracking_url} size="xs">
              Track on {shipment.carrier ? CARRIER_LABEL[shipment.carrier] : "carrier"}
            </LinkOut>
          )}
        </div>

        {shipment.check_error && (
          <p className="text-xs text-warning">Last check: {shipment.check_error}</p>
        )}
        {shipment.last_checked_at && (
          <p className="text-xs text-muted-foreground">
            Checked {fmtDate(shipment.last_checked_at)}
          </p>
        )}
        {update.error && <Alert variant="danger">{String(update.error)}</Alert>}
      </CardContent>
    </Card>
  );
}

// ── Catalog ─────────────────────────────────────────────────────────────────

function CatalogPanel() {
  const items = useItems();
  const purchases = usePurchases();
  const save = useSaveItem();
  const upload = useUploadPhoto();
  const fileRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState<Item | null>(null);
  const [name, setName] = useState("");
  const [cost, setCost] = useState<number | null>(null);
  const [threshold, setThreshold] = useState<number | null>(3);
  const [description, setDescription] = useState("");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add a swag item</CardTitle>
          <p className="text-sm text-muted-foreground">
            The kind of thing, not a specific order — “Minky Blanket”, not “the
            20 blankets we bought in March”. Orders go under Purchases.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field
              id="item-name"
              label="Item name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <NumberInput
              id="item-cost"
              label="Typical cost"
              prefix="$"
              value={cost}
              onChange={setCost}
            />
            <NumberInput
              id="item-threshold"
              label="Flag as low at"
              maxDecimals={0}
              value={threshold}
              onChange={setThreshold}
              helper="Summary shows a Low badge at or below this"
            />
          </div>
          <Textarea
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file, { onSuccess: (r) => setPhotoUrl(r.url) });
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={upload.isPending}
            >
              <Upload className="mr-1 h-4 w-4" /> Add photo
            </Button>
            {photoUrl && (
              <img
                src={photoUrl}
                alt="Item"
                className="h-14 w-14 rounded-lg border object-cover"
              />
            )}
            <Button
              className="ml-auto"
              disabled={!name.trim() || save.isPending}
              onClick={() =>
                save.mutate(
                  {
                    body: {
                      name: name.trim(),
                      description: description || null,
                      photo_url: photoUrl,
                      default_unit_cost: cost,
                      low_stock_threshold: threshold ?? 0,
                      is_active: true,
                      sort_order: 0,
                    },
                  },
                  {
                    onSuccess: () => {
                      setName("");
                      setCost(null);
                      setDescription("");
                      setPhotoUrl(null);
                    },
                  },
                )
              }
            >
              Add item
            </Button>
          </div>
          {save.error && <Alert variant="danger">{String(save.error)}</Alert>}
        </CardContent>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(items.data ?? []).map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            lots={(purchases.data ?? []).filter((p) => p.item_id === item.id)}
            onEdit={() => setEditing(item)}
          />
        ))}
      </div>

      <ItemDialog
        item={editing}
        open={editing !== null}
        onOpenChange={(next) => !next && setEditing(null)}
      />
    </div>
  );
}

/** One catalog item: what it is, what it cost across orders, and an Edit door. */
function ItemCard({
  item,
  lots,
  onEdit,
}: {
  item: Item;
  lots: Purchase[];
  onEdit: () => void;
}) {
  const prices = lots.map((l) => l.unit_cost).sort((a, b) => a - b);
  const units = lots.reduce((sum, l) => sum + l.quantity, 0);
  const spend = lots.reduce((sum, l) => sum + l.unit_cost * l.quantity, 0);
  // Weighted by quantity: what a unit of this actually cost us on average,
  // not the average of the order prices (which would let a 1-unit order
  // outweigh a 36-unit one).
  const weighted = units > 0 ? spend / units : null;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-4">
        <div className="flex items-center gap-3">
          {item.photo_url ? (
            <img
              src={item.photo_url}
              alt={item.name}
              className="h-12 w-12 rounded-lg border object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-lg border bg-muted">
              <Package className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{item.name}</p>
            <p className="text-xs text-muted-foreground">
              {weighted != null
                ? `${fmtMoney(weighted)} average unit cost`
                : item.default_unit_cost != null
                  ? `${fmtMoney(item.default_unit_cost)} typical`
                  : "Never ordered"}
              {!item.is_active && " · inactive"}
            </p>
          </div>
          <IconButton title={`Edit ${item.name}`} onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </IconButton>
        </div>

        {lots.length > 0 && <PriceDistribution lots={lots} prices={prices} />}
      </CardContent>
    </Card>
  );
}

/**
 * What we've paid for this item across orders.
 *
 * Shown as a strip rather than a number because the interesting thing is the
 * SPREAD: the blankets were bought at one price twice, so a single "average"
 * says everything; if a later order comes in 30% higher, that is the thing
 * worth seeing before the next purchase, and an average would hide it.
 */
function PriceDistribution({ lots, prices }: { lots: Purchase[]; prices: number[] }) {
  const min = prices[0]!;
  const max = prices[prices.length - 1]!;
  const spread = max - min;

  return (
    <div className="flex flex-col gap-1.5 border-t pt-3">
      <div className="flex items-baseline justify-between text-[11px] uppercase tracking-wider text-muted-foreground">
        <span>Order prices</span>
        <span>
          {lots.length} order{lots.length === 1 ? "" : "s"}
        </span>
      </div>

      {spread === 0 ? (
        <p className="text-sm">
          <span className="font-medium tabular-nums">{fmtMoney(min)}</span>{" "}
          <span className="text-muted-foreground">
            every time — {lots.length === 1 ? "one order" : "no change across orders"}
          </span>
        </p>
      ) : (
        <>
          {/* Each order sits at its price along the min–max span, so a cluster
              at one end reads as a cluster rather than as a mean. */}
          <div className="relative h-6">
            <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-border" />
            {lots.map((lot) => {
              const pct = ((lot.unit_cost - min) / spread) * 100;
              return (
                <span
                  key={lot.id}
                  title={`${fmtMoney(lot.unit_cost)} each · ${lot.quantity} units${
                    lot.purchase_year ? ` · ${lot.purchase_year}` : ""
                  }`}
                  className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand ring-2 ring-background"
                  style={{ left: `${pct}%` }}
                />
              );
            })}
          </div>
          <div className="flex justify-between text-xs tabular-nums">
            <span className="font-medium">{fmtMoney(min)}</span>
            <span className="text-warning">+{fmtMoney(spread)} spread</span>
            <span className="font-medium">{fmtMoney(max)}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ── Import ──────────────────────────────────────────────────────────────────

function ImportPanel() {
  const importWorkbook = useImportWorkbook();
  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Import the old spreadsheet</CardTitle>
        <p className="text-sm text-muted-foreground">
          Loads <em>Major Swag Items Usage.xlsx</em> — items, purchase lots
          (grouped by item, year and cost), every giveaway, and the shipments.
          Rows that shared one shipping cost across several recipients become one
          box, so the freight isn’t multiplied. Rows with a missing recipient or
          an unmatched department import as written and get flagged for review
          rather than guessed at. Safe to run twice: lots that already exist are
          skipped.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xlsm"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) importWorkbook.mutate(file);
          }}
        />
        <Button
          className="self-start"
          disabled={importWorkbook.isPending}
          onClick={() => fileRef.current?.click()}
        >
          <Upload className="mr-1 h-4 w-4" />
          {importWorkbook.isPending ? "Importing…" : "Choose workbook"}
        </Button>

        {importWorkbook.error && (
          <Alert variant="danger">{String(importWorkbook.error)}</Alert>
        )}
        {importWorkbook.data && (
          <Alert variant={importWorkbook.data.imported ? "success" : "info"}>
            {importWorkbook.data.imported ? (
              <div className="flex flex-col gap-1">
                <strong>
                  Imported {importWorkbook.data.rows_read} rows —{" "}
                  {importWorkbook.data.lots_created} purchase orders,{" "}
                  {importWorkbook.data.disbursements} giveaways,{" "}
                  {importWorkbook.data.shipments} shipments,{" "}
                  {importWorkbook.data.units_on_hand} units on hand.
                </strong>
                {(importWorkbook.data.needs_review ?? 0) > 0 && (
                  <span>
                    {importWorkbook.data.needs_review} row(s) flagged for review —
                    see the Detail tab’s “Needs review” filter.
                  </span>
                )}
              </div>
            ) : (
              importWorkbook.data.reason
            )}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
